import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "../components/Note";
import api from "../api";

export default function WhatsappGroupContacts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [integration, setIntegration] = useState({ mode: "unknown", is_connected: false });
  const [groups, setGroups] = useState([]);
  const [dmNameById, setDmNameById] = useState(new Map()); // id '@c.us' -> nombre
  const [selectedGroup, setSelectedGroup] = useState("");
  const [participants, setParticipants] = useState([]);
  const [lists, setLists] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [newListName, setNewListName] = useState("");
  const [destListId, setDestListId] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [selectedNums, setSelectedNums] = useState(() => new Set()); // números seleccionados

  // Cargar modo de integración y, si corresponde, los grupos vía Web.js
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data: status } = await api.get('integration/connection-status/');
        const mode = status?.mode || 'unknown';
        const is_connected = !!status?.is_connected;
        if (!cancelled) setIntegration({ mode, is_connected });
        if (mode === 'coexistencia') {
          // Coexistencia Meta no expone grupos: mostrar mensaje y no llamar a /whatsapp/chats/
          if (!cancelled) setError("Tu modo es Coexistencia Meta. La importación de grupos requiere la integración No Oficial (QR). Usa 'Alimentar mi BD' para capturar contactos.");
          return;
        }
        // Modo No Oficial (QR): cargar grupos
        const { data } = await api.get("whatsapp/chats/");
        const arr = Array.isArray(data) ? data : [];
        const onlyGroups = arr.filter((c) => c.isGroup);
        if (!cancelled) setGroups(onlyGroups);
        const map = new Map();
        for (const c of arr) {
          if (!c.isGroup && c.id) {
            const nm = c.contactName || c.name || null;
            if (nm) map.set(String(c.id), nm);
          }
        }
        if (!cancelled) setDmNameById(map);
      } catch (e) {
        if (!cancelled) setError("No se pudieron cargar los grupos de WhatsApp.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Cargar listas existentes
  useEffect(() => {
    api.get("listas/")
      .then((res) => setLists(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  // Cargar contactos existentes (para mostrar nombre si coincide el número)
  useEffect(() => {
    api.get("contactos/")
      .then((res) => setAllContacts(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  // Cargar participantes al seleccionar un grupo
  useEffect(() => {
    if (!selectedGroup) { setParticipants([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`whatsapp/group/${encodeURIComponent(selectedGroup)}/participants/`);
        if (!cancelled) setParticipants(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setParticipants([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedGroup]);

  // Reset de selección al cambiar de grupo
  useEffect(() => { setSelectedNums(new Set()); }, [selectedGroup]);

  // Helpers de nombres/normalización
  const normalizePhone = (s) => String(s || "").replace(/\D/g, "");
  const phoneKeys = (digits) => {
    const d = normalizePhone(digits);
    const keys = [];
    if (d) {
      keys.push(d);
      if (d.length >= 10) keys.push(d.slice(-10));
      if (d.length >= 9) keys.push(d.slice(-9));
    }
    return keys;
  };
  const nameByPhone = useMemo(() => {
    const m = new Map();
    for (const c of allContacts) {
      const base = normalizePhone(c.phone);
      const n = (c.full_name || `${c.first_name || ""} ${c.last_name || ""}`).trim();
      if (!base) continue;
      for (const k of phoneKeys(base)) {
        if (!m.has(k)) m.set(k, n || "");
      }
    }
    return m;
  }, [allContacts]);

  // Grupo seleccionado (para obtener su nombre)
  const selectedGroupObj = useMemo(() => groups.find(g => g.id === selectedGroup), [groups, selectedGroup]);
  const selectedGroupName = selectedGroupObj?.groupName || selectedGroupObj?.name || selectedGroupObj?.id || "";

  // Participantes enriquecidos con nombre conocido (si ya existe en Contactos)
  const participantsDisplay = useMemo(() => participants.map(p => {
    const keys = phoneKeys(p.number);
    let bestName = "";
    for (const k of keys) {
      const v = nameByPhone.get(k);
      if (v) { bestName = v; break; }
    }
    if (!bestName) {
      // intentar por id en mapa de DMs
      const byId = dmNameById.get(String(p.id || '')) || null;
      bestName = byId || p.name || "";
    }
    // Importante: NO usar el número como nombre; si no hay nombre, dejar vacío
    return { ...p, displayName: bestName };
  }), [participants, nameByPhone, dmNameById]);

  const rowsPreview = useMemo(() => participantsDisplay.map(p => ({
    // Si no hay nombre, guardar vacío (no el número)
    full_name: p.displayName || "",
    phone: p.number,
    email: "",
    channel: "Whatsapp",
    note: "",
  })), [participantsDisplay]);

  const saveToList = async () => {
    if (!participantsDisplay.length) { setStatus("Selecciona un grupo con participantes"); return; }
    const selected = participantsDisplay.filter(p => selectedNums.has(normalizePhone(p.number)) || selectedNums.has(String(p.number)));
    if (selected.length === 0) { setStatus("Selecciona al menos un contacto"); return; }
    setSaving(true);
    setStatus("");
    try {
      let listId = null;
      if (mode === "new") {
        const name = (newListName || "").trim();
        if (!name) { setStatus("Escribe un nombre para la nueva lista"); setSaving(false); return; }
        // Crear lista vacía
        const { data } = await api.post("listas/", { name, contacts: [] });
        listId = data?.id;
      } else {
        listId = destListId;
      }
      if (!listId) { setStatus("Selecciona una lista destino"); setSaving(false); return; }

      // 1) Traer contactos existentes y crear los que falten (solo seleccionados)
      const { data: allC } = await api.get("contactos/");
      const existingByPhone = new Map();
      for (const c of (Array.isArray(allC) ? allC : [])) {
        const base = normalizePhone(c.phone);
        if (!base) continue;
        for (const k of phoneKeys(base)) {
          if (!existingByPhone.has(k)) existingByPhone.set(k, c);
        }
      }

      const idsToAdd = [];
      for (const p of selected) {
        // Buscar si ya existe por cualquiera de las variantes
        let found = null;
        for (const k of phoneKeys(p.number)) {
          const c = existingByPhone.get(k);
          if (c) { found = c; break; }
        }
        if (found) {
          idsToAdd.push(found.id);
          continue;
        }
        // Crear contacto nuevo con nombre válido (nunca número)
        const payload = {
          full_name: (p.displayName || "Sin nombre"),
          phone: normalizePhone(p.number),
          email: "",
          channel: "Whatsapp",
          note: "",
        };
        try {
          const { data: created } = await api.post("contactos/", payload);
          idsToAdd.push(created.id);
        } catch (_) { /* continuar */ }
      }

      // 2) Unir a la lista destino
      const { data: l } = await api.get(`listas/${listId}/`);
      const before = new Set(l.contacts || []);
      const next = Array.from(new Set([...(l.contacts || []), ...idsToAdd]));
      await api.patch(`listas/${listId}/`, { contacts: next });
      const addedCount = next.filter(id => !before.has(id)).length;
      setStatus(`Agregados ${addedCount} contacto(s) a la lista`);
    } catch (e) {
      setStatus("No se pudo guardar en la lista");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-layout" style={{ background: "#ffffff", minHeight: "100vh" }}>
      <Sidebar />
      <main className="dashboard-main" style={{ background: "#ffffff", color: "#111827" }}>
        <div className="dashboard-header" style={{ color: "#111827", fontSize: "1.7rem", fontWeight: 600, marginBottom: "1.5rem" }}>
          Contactos de WhatsApp
        </div>
        <section style={{ background: "#ffffff", borderRadius: 18, boxShadow: "0 2px 12px rgba(44,62,80,0.18)", padding: "1rem 1.5rem", maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/contactos')} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.4rem 0.7rem' }}>← Volver</button>
            {integration.mode === 'coexistencia' ? (
              <span style={{ color:'#64748b' }}>Modo Coexistencia Meta activo — grupos no disponibles.</span>
            ) : loading ? (
              <span style={{ color:'#64748b' }}>Cargando grupos…</span>
            ) : (
              <select value={selectedGroup} onChange={(e)=> setSelectedGroup(e.target.value)} style={{ background:'#fff', color:'#111827', border:'1px solid #e5e7eb', borderRadius:8, padding:'0.45rem 0.6rem', minWidth:260 }}>
                <option value="">Selecciona un grupo…</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.groupName || g.name || g.id}</option>
                ))}
              </select>
            )}
          </div>
          {error && <div style={{ color:'#b91c1c', marginBottom:8 }}>{error}</div>}
          {integration.mode === 'coexistencia' && (
            <div style={{ color:'#334155', marginBottom:12 }}>
              Sugerencia: usa <a href="/conexion" style={{ color:'#0ea5e9' }}>Alimentar mi BD</a> para crear un formulario público y captar contactos.
            </div>
          )}
          {selectedGroup && (
            <div style={{ marginTop: 8 }}>
              <div style={{ marginBottom: 8, color:'#334155' }}>Participantes: {participants.length}</div>
              <div style={{ maxHeight: 300, overflowY: 'auto', border:'1px solid #e5e7eb', borderRadius: 8 }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#f8fafc' }}>
                      <th style={{ padding:'8px 10px' }}>
                        <input
                          type="checkbox"
                          checked={participantsDisplay.length>0 && participantsDisplay.every(p=> selectedNums.has(normalizePhone(p.number)) )}
                          onChange={(e)=>{
                            const all = new Set(selectedNums);
                            const shouldSelect = e.target.checked;
                            for (const p of participantsDisplay) {
                              const k = normalizePhone(p.number);
                              if (shouldSelect) all.add(k); else all.delete(k);
                            }
                            setSelectedNums(all);
                          }}
                          aria-label="Seleccionar todos"
                        />
                      </th>
                      <th style={{ textAlign:'left', padding:'8px 10px', color:'#334155' }}>Nombre</th>
                      <th style={{ textAlign:'left', padding:'8px 10px', color:'#334155' }}>Teléfono</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participantsDisplay.map(p => (
                      <tr key={p.id}>
                        <td style={{ padding:'6px 10px' }}>
                          <input
                            type="checkbox"
                            checked={selectedNums.has(normalizePhone(p.number))}
                            onChange={(e)=>{
                              const k = normalizePhone(p.number);
                              const s = new Set(selectedNums);
                              if (e.target.checked) s.add(k); else s.delete(k);
                              setSelectedNums(s);
                            }}
                            aria-label={`Seleccionar ${p.displayName || p.number}`}
                          />
                        </td>
                        <td style={{ padding:'6px 10px' }}>{p.displayName || '(sin nombre)'}</td>
                        <td style={{ padding:'6px 10px' }}>{p.number}</td>
                      </tr>
                    ))}
                    {participants.length === 0 && (
                      <tr><td colSpan={3} style={{ padding: '1rem', textAlign:'center', color:'#94a3b8' }}>Este grupo no tiene participantes visibles o la sesión no está lista.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 16, display:'flex', gap: 12, alignItems:'center', flexWrap:'wrap' }}>
                <label>
                  <input type="radio" name="mode" value="existing" checked={mode==='existing'} onChange={()=> setMode('existing')} />
                  <span style={{ marginLeft: 6 }}>Agregar a lista existente</span>
                </label>
                <label>
                  <input type="radio" name="mode" value="new" checked={mode==='new'} onChange={()=> setMode('new')} />
                  <span style={{ marginLeft: 6 }}>Crear nueva lista</span>
                </label>

                {mode === 'existing' ? (
                  <select value={destListId} onChange={(e)=> setDestListId(e.target.value)} style={{ background:'#fff', color:'#111827', border:'1px solid #e5e7eb', borderRadius:8, padding:'0.45rem 0.6rem', minWidth:220 }}>
                    <option value="">Selecciona una lista…</option>
                    {lists.map(l => (<option key={l.id} value={l.id}>{l.name}</option>))}
                  </select>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <input value={newListName} onChange={(e)=> setNewListName(e.target.value)} placeholder="Nombre de la nueva lista" style={{ background:'#fff', color:'#111827', border:'1px solid #e5e7eb', borderRadius:8, padding:'0.45rem 0.6rem', minWidth:240 }} />
                    <button type="button" onClick={()=> setNewListName(selectedGroupName)} disabled={!selectedGroupName} title="Usar el nombre del grupo de WhatsApp" style={{ background:'#f3f4f6', color:'#374151', border:'1px solid #e5e7eb', borderRadius:8, padding:'0.45rem 0.6rem', opacity: selectedGroupName?1:0.6 }}>
                      Usar nombre del grupo
                    </button>
                  </div>
                )}

                <button disabled={saving || participants.length===0} onClick={saveToList} style={{ background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8, padding:'0.5rem 0.9rem', fontWeight:600, cursor:'pointer', opacity: (saving||participants.length===0)?0.7:1 }}>
                  {saving ? 'Guardando…' : 'Guardar en Contactos'}
                </button>
                {status && <span style={{ color: status.includes('No se pudo')? '#b91c1c':'#059669' }}>{status}</span>}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
